// src/components/extraction/MultiImageCapture.jsx
// Sammelt mehrere Einzelbilder für eine mehrseitige Rechnung:
// - Mobilgeräte: Foto für Foto über die Kamera (CameraCaptureButton), wie bisher.
// - Desktop: ein Dateidialog mit Mehrfachauswahl - z.B. für zwei zusammengehörige
//   Einzelbilder/Scans, die schon als Dateien vorliegen.
// In beiden Fällen werden die Bilder nur gesammelt/angezeigt (Thumbnails). Die
// eigentliche PDF-Erzeugung inkl. EXIF-Rotationskorrektur passiert jetzt
// serverseitig im Endpoint POST /extract/default/images
// (DatenLeser.Preprocessor.ImagesToPdfConverter) - deshalb keine jsPDF-Abhängigkeit
// mehr hier, "Fertig" ist rein synchron.
import React, { useState, useEffect, useRef } from 'react'
import CameraCaptureButton from './CameraCaptureButton'
import useIsDesktop from './useIsDesktop'

function MultiImageCapture ({
  onImagesReady,
  onAbbrechen,
  disabled,
  open,
  resetSignal,
  erzwingeKamera
}) {
  const isDesktop = useIsDesktop()
  // Erzwungener Kamera-Modus (z.B. Tablet, das fälschlich als Desktop erkannt wird) schaltet
  // trotz breitem Viewport auf die Kamera-pro-Seite-Variante um.
  const kameraModus = !isDesktop || erzwingeKamera
  const [photos, setPhotos] = useState([])
  const desktopInputRef = useRef(null)
  const isFirstRender = useRef(true)

  const resetPhotos = () => {
    setPhotos(prev => {
      prev.forEach(p => URL.revokeObjectURL(p.previewUrl))
      return []
    })
  }

  // Automatisch leeren: neue Einzeldatei ausgewählt ODER neue Mehrseiten-Aufnahme
  // begonnen (beides erhöht resetSignal in Extraction.jsx).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    resetPhotos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal])

  useEffect(() => {
    return () => {
      setPhotos(prev => {
        prev.forEach(p => URL.revokeObjectURL(p.previewUrl))
        return prev
      })
    }
  }, [])

  const addPhoto = file => {
    setPhotos(prev => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        file,
        previewUrl: URL.createObjectURL(file)
      }
    ])
  }

  const handleDesktopFilesSelected = e => {
    Array.from(e.target.files || []).forEach(addPhoto)
    e.target.value = '' // erlaubt erneute Auswahl derselben Dateien
  }

  const removePhoto = id => {
    setPhotos(prev => {
      const removed = prev.find(p => p.id === id)
      if (removed) URL.revokeObjectURL(removed.previewUrl)
      return prev.filter(p => p.id !== id)
    })
  }

  const handleFinish = () => {
    if (photos.length === 0) return
    onImagesReady(photos.map(p => p.file))
  }

  const handleAbbrechen = () => {
    resetPhotos()
    onAbbrechen?.()
  }

  if (!open && photos.length === 0) return null

  return (
    <div className='multi-photo-panel'>
      {open && (
        <p className='multi-photo-hint'>
          {kameraModus
            ? 'Für mehrseitige Rechnungen: pro Seite ein Foto aufnehmen, danach auf "Fertig" klicken.'
            : 'Für mehrseitige Rechnungen: mehrere Bilddateien auswählen, danach auf "Fertig" klicken.'}
        </p>
      )}

      {photos.length > 0 && (
        <div className='multi-photo-thumbs'>
          {photos.map((p, index) => (
            <div key={p.id} className='multi-photo-thumb'>
              <img src={p.previewUrl} alt={`Seite ${index + 1}`} />
              <span className='multi-photo-page-number'>{index + 1}</span>
              {open && (
                <button
                  type='button'
                  className='multi-photo-remove'
                  onClick={() => removePhoto(p.id)}
                  aria-label={`Seite ${index + 1} entfernen`}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className='multi-photo-actions'>
          {kameraModus ? (
            <CameraCaptureButton
              onFileSelected={addPhoto}
              disabled={disabled}
              label={
                photos.length === 0
                  ? '📷 Erste Seite aufnehmen'
                  : '📷 Weitere Seite aufnehmen'
              }
            />
          ) : (
            <>
              <button
                type='button'
                className='btn'
                onClick={() => desktopInputRef.current?.click()}
                disabled={disabled}
              >
                🖼️ Bilder auswählen
              </button>
              <input
                ref={desktopInputRef}
                type='file'
                accept='image/*'
                multiple
                style={{ display: 'none' }}
                onChange={handleDesktopFilesSelected}
              />
            </>
          )}
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleFinish}
            disabled={photos.length === 0}
          >
            Fertig
          </button>
          {photos.length > 0 && (
            <button type='button' className='btn' onClick={resetPhotos}>
              Zurücksetzen
            </button>
          )}
          <button type='button' className='btn' onClick={handleAbbrechen}>
            Abbrechen
          </button>
        </div>
      )}
    </div>
  )
}

export default MultiImageCapture
